/**
 * The workspace run timeline as a bar chart over real time.
 *
 * WHY A CHART INSTEAD OF THE VERTICAL RAIL. The rail answered "what happened, in order" and was
 * honest about causality, but it spent a screen per handful of events and said nothing about
 * RATE - a workspace applying twenty times on Tuesday and nothing since looked identical to one
 * ticking over steadily, because position on the rail carries only sequence, never elapsed time.
 * Here the x axis IS time, so a cluster looks like a cluster and a quiet fortnight looks quiet,
 * and the refusals land in the same bar as the runs they interrupted.
 *
 * WHY BARS, AND WHY STACKED. These are counted events in a window, not a continuous quantity, so
 * a bar is the honest mark: it says "four things happened in these twelve hours" without drawing
 * a slope between two windows that implies a rate in between. Stacking works because the three
 * outcomes are disjoint - a refused apply never acquires a lock, so it is never also a run - which
 * makes the bar's full height the window's event count, the same number the rail below reports.
 * Grouping them instead would put seventy-odd bars across 300 viewBox units, each too thin to hit.
 *
 * The one thing the bar height does NOT count is a `warn` decision, which is on the rail but on no
 * series - same as the tab's stat strip, which counts runs, refusals and overrides and nothing else.
 *
 * The rail is not gone: clicking a bar selects that window and the caller renders its entries
 * underneath, which is where the reasons, the actors and the attestation links still live. The
 * chart is the index; the rail is the detail.
 */
import { useState } from 'react'
import { axisTick, bucketLabel, bucketSummary, stepLabel, tickForm, type TimelineBucket } from '@/features/state/lib/runTimeline'
import { formatCount } from '@/lib/format'

export interface RunTimelineChartProps {
  /** Oldest first, zero-filled - see bucketTimeline. */
  buckets: TimelineBucket[]
  step: number
  selected: number | null
  onSelect: (index: number | null) => void
}

interface Series {
  key: 'runs' | 'blocks' | 'overrides'
  label: string
  color: string
}

/** Stacking order, bottom first: runs are the floor the refusals sit on. */
const SERIES: Series[] = [
  { key: 'runs', label: 'runs', color: 'var(--accent)' },
  { key: 'blocks', label: 'refusals', color: 'var(--red)' },
  { key: 'overrides', label: 'overrides', color: 'var(--amber)' },
]

const VB_W = 300
const VB_H = 100
const CHART_H = 140
/** Share of a bucket's slot left empty, each side. Bars have to read as separate windows. */
const GAP = 0.16

export function RunTimelineChart({ buckets, step, selected, onSelect }: RunTimelineChartProps) {
  const [hovered, setHovered] = useState<number | null>(null)

  const n = buckets.length
  if (n === 0) return null

  const totalOf = (b: TimelineBucket) => b.runs + b.blocks + b.overrides
  // The y scale is the STACK, not the tallest single series - otherwise a bar would overflow the
  // plot the moment one window held both runs and refusals.
  const max = Math.max(1, ...buckets.map(totalOf))
  // runs keeps its legend entry even at zero: its absence would read as "no data" rather than
  // "nothing ever acquired a lock here", which is the more interesting of the two.
  const drawn = SERIES.filter((s) => s.key === 'runs' || buckets.some((b) => b[s.key] > 0))

  const slot = VB_W / n
  const barW = Math.max(slot * (1 - 2 * GAP), 0.6)
  const barX = (i: number) => i * slot + (slot - barW) / 2
  // Bars sit on the floor of the viewBox, so the "0" label lines up with the baseline and the
  // busiest window reaches the top of the plot rather than a padded ceiling.
  const yAt = (v: number) => VB_H - (v / max) * VB_H

  // The window the axis covers, which decides whether a tick carries a clock, a date or both.
  const span = buckets[n - 1]!.end - buckets[0]!.start
  // Three ticks, not five, once each one spells out "21 Oct, 13:00" - at phone width five of
  // those overlapped into one unreadable smear.
  const ticks = tickForm(step, span) === 'datetime' ? 3 : 5
  const tickIdx = n <= ticks
    ? buckets.map((_, i) => i)
    : [...new Set(Array.from({ length: ticks }, (_, k) => Math.round(((n - 1) * k) / (ticks - 1))))]
  const focus = hovered ?? selected
  const totals = {
    runs: buckets.reduce((a, b) => a + b.runs, 0),
    blocks: buckets.reduce((a, b) => a + b.blocks, 0),
    overrides: buckets.reduce((a, b) => a + b.overrides, 0),
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 8 }}>
        <div
          aria-hidden
          style={{
            display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: CHART_H,
            fontFamily: 'var(--mono)', fontSize: '0.55rem', color: 'var(--text3)', textAlign: 'right', flexShrink: 0,
          }}
        >
          <span>{formatCount(max)}</span>
          {/* Empty rather than absent when max is 2 or 3: "3 / 2 / 0" prints a midpoint that is
              not the midpoint, and dropping the element would re-space the other two. */}
          <span>{max >= 4 ? formatCount(Math.round(max / 2)) : ''}</span>
          <span>0</span>
        </div>

        <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
          {/* Anchored above the plot (translateY(-100%)); the caller reserves the room. */}
          {focus !== null && buckets[focus] && (
            <div
              style={{
                position: 'absolute',
                left: `${Math.min(96, Math.max(4, ((focus + 0.5) / n) * 100))}%`,
                top: -2,
                transform: `translate(${focus < n / 6 ? '0' : focus > (5 * n) / 6 ? '-100%' : '-50%'}, -100%)`,
                background: 'var(--bg2)',
                border: '1px solid var(--border)',
                borderRadius: 4,
                padding: '5px 8px',
                fontFamily: 'var(--mono)',
                fontSize: '0.62rem',
                whiteSpace: 'nowrap',
                pointerEvents: 'none',
                zIndex: 1,
              }}
            >
              <div style={{ color: 'var(--text2)' }}>{bucketLabel(buckets[focus]!, step)}</div>
              <div style={{ fontWeight: 700 }}>{bucketSummary(buckets[focus]!) ?? 'nothing'}</div>
            </div>
          )}

          <svg
            viewBox={`0 0 ${VB_W} ${VB_H}`}
            preserveAspectRatio="none"
            width="100%"
            height={CHART_H}
            role="group"
            aria-label={`Runs and policy decisions per ${stepLabel(step)} window: ${totals.runs} runs, ${totals.blocks} refused, ${totals.overrides} overridden.`}
            style={{ overflow: 'visible' }}
            onMouseLeave={() => setHovered(null)}
          >
            {/* The whole slot lights up, not just the bar: an empty window is selectable too, and
                a hover that produced no highlight reads as a dead control. */}
            {focus !== null && focus !== selected && (
              <rect x={focus * slot} y={0} width={slot} height={VB_H} fill="var(--accent-d)" />
            )}
            {selected !== null && buckets[selected] && (
              <rect x={selected * slot} y={0} width={slot} height={VB_H} fill="var(--accent-d2)" />
            )}

            {buckets.map((b, i) => {
              let acc = 0
              return SERIES.map((s) => {
                const v = b[s.key]
                if (v === 0) return null
                const y = yAt(acc + v)
                acc += v
                return (
                  <rect
                    key={`${s.key}-${b.start}`}
                    x={barX(i)} y={y} width={barW} height={(v / max) * VB_H}
                    fill={s.color}
                  />
                )
              })
            })}

            {/* The zero line, drawn over the bars' feet so a one-event bar still has something to
                sit on when the plot is otherwise empty. */}
            <line
              x1={0} y1={VB_H} x2={VB_W} y2={VB_H}
              stroke="var(--border)" strokeWidth={1} vectorEffect="non-scaling-stroke"
            />

            {/* Full-height hit targets: the click that selects a window has to land somewhere even
                where the bar is short or absent. Focusable so the selection is keyboard-reachable. */}
            {buckets.map((b, i) => (
              <rect
                key={`hit-${b.start}`}
                x={i * slot}
                y={0}
                width={slot}
                height={VB_H}
                fill="transparent"
                role="button"
                tabIndex={0}
                aria-pressed={selected === i}
                aria-label={`${bucketLabel(b, step)}: ${bucketSummary(b) ?? 'nothing'}`}
                style={{ cursor: b.entries.length > 0 ? 'pointer' : 'default' }}
                // Keeps the click from focusing the rect: the selection already has a visible
                // band, and Chrome paints the focus outline on a mouse click too.
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setHovered(i)}
                onFocus={() => setHovered(i)}
                onBlur={() => setHovered(null)}
                onClick={() => onSelect(selected === i ? null : i)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onSelect(selected === i ? null : i)
                  }
                }}
              />
            ))}
          </svg>

          {/* Ticks sit under the CENTRE of their bar, not the bucket boundary - a label under the
              edge belongs to neither of the two bars it falls between. */}
          <div aria-hidden style={{ position: 'relative', height: 12, marginTop: 6 }}>
            {tickIdx.map((idx) => (
              <span
                key={idx}
                style={{
                  position: 'absolute',
                  left: `${((idx + 0.5) / n) * 100}%`,
                  transform: `translateX(${idx === 0 ? '-12%' : idx === n - 1 ? '-88%' : '-50%'})`,
                  fontFamily: 'var(--mono)',
                  fontSize: '0.55rem',
                  color: 'var(--text3)',
                  whiteSpace: 'nowrap',
                }}
              >
                {axisTick(buckets[idx]!.start, step, span)}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 10 }}>
        {drawn.map((s) => (
          <span
            key={s.key}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.68rem', color: 'var(--text2)' }}
          >
            <span aria-hidden style={{ width: 9, height: 9, background: s.color, borderRadius: 2 }} />
            {s.label}
          </span>
        ))}
      </div>

      <div style={{ fontFamily: 'var(--mono)', fontSize: '0.6rem', color: 'var(--text3)', marginTop: 8 }}>
        // One bar per {stepLabel(step)} window across this workspace's recorded history, stacked by
        outcome. A window with nothing in it has no bar, so a gap is a real gap. Click a bar for the
        events inside it.
      </div>
    </div>
  )
}
